import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { FiBriefcase, FiTarget, FiPieChart, FiArrowRight, FiFilter, FiAlertCircle, FiClock, FiMapPin, FiUsers, FiCalendar } from 'react-icons/fi';
import { useAuth } from '../../finance/auth/AuthContext';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts';

const API_BASE_URL = process.env.REACT_APP_BASE_URL || 'http://localhost:8080';
const LEAD_STAGES = ['LEAD', 'TALKS', 'MEETING', 'NEGOTIATIONS', 'MQL', 'SQL', 'CONVERSION_TO_CLIENT'];
const STAGE_COLORS = { 'LEAD': '#94a3b8', 'TALKS': '#60a5fa', 'MEETING': '#3b82f6', 'NEGOTIATIONS': '#2563eb', 'MQL': '#8b5cf6', 'SQL': '#6366f1', 'CONVERSION_TO_CLIENT': '#1e40af' };

export default function SalesDashboard() {
  const { user, role, username } = useAuth();
  
  // Basic & Filter states
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ stage: '', region: '', status: '' });
  
  // Action Center states
  const [todayFollowups, setTodayFollowups] = useState([]);
  const [overdueFollowups, setOverdueFollowups] = useState([]);

  // Admin stats states
  const [detailedStats, setDetailedStats] = useState(null);
  const [regionStats, setRegionStats] = useState([]);
  const [performance, setPerformance] = useState([]);
  const [timelineStats, setTimelineStats] = useState(null);

  const visibleRole = role?.replace('ROLE_', '').toUpperCase() || 'USER';
  const isAdmin = visibleRole.includes('ADMIN') || visibleRole.includes('SUPERADMIN') || visibleRole.includes('TESTER') || user?.isSuperadmin;

  const fetchDashboardData = async () => {
    if (!user?.token) return;

    try {
      setLoading(true);
      const headers = { Authorization: `${user?.tokenType || 'Bearer'} ${user.token}` };

      // 1. Build Filter URL
      let leadsUrl = `${API_BASE_URL}/sales/leads`;
      if (filters.stage || filters.region || filters.status) {
        const params = new URLSearchParams();
        if (filters.stage) params.append('stage', filters.stage);
        if (filters.region) params.append('region', filters.region);
        if (filters.status) params.append('status', filters.status);
        leadsUrl = `${API_BASE_URL}/sales/leads/filter?${params.toString()}`;
      }

      // 2. Fetch parallel requests
      const requests = [
        axios.get(leadsUrl, { headers }),
        axios.get(`${API_BASE_URL}/sales/leads/followup/today`, { headers }).catch(() => ({ data: { data: [] } })),
        axios.get(`${API_BASE_URL}/sales/leads/followup/overdue`, { headers }).catch(() => ({ data: { data: [] } }))
      ];

      if (isAdmin) {
        requests.push(axios.get(`${API_BASE_URL}/sales/leads/stats/detailed`, { headers }).catch(() => ({ data: { data: {} } })));
        requests.push(axios.get(`${API_BASE_URL}/sales/leads/stats/region`, { headers }).catch(() => ({ data: { data: [] } })));
        requests.push(axios.get(`${API_BASE_URL}/sales/leads/performance`, { headers }).catch(() => ({ data: { data: [] } })));
        requests.push(axios.get(`${API_BASE_URL}/sales/leads/stats/timeline`, { headers }).catch(() => ({ data: { data: {} } })));
      }

      const results = await Promise.all(requests);
      
      setLeads(results[0]?.data?.data || []);
      setTodayFollowups(results[1]?.data?.data || []);
      setOverdueFollowups(results[2]?.data?.data || []);

      if (isAdmin) {
        setDetailedStats(results[3]?.data?.data || {});
        setRegionStats(results[4]?.data?.data || []);
        setPerformance(results[5]?.data?.data || []);
        setTimelineStats(results[6]?.data?.data || {});
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
    // eslint-disable-next-line
  }, [user?.token, isAdmin, filters]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const activeLeadsCount = leads.filter(l => l.leadStatus !== 'CONVERSION_TO_CLIENT').length;
  const wonLeadsCount = leads.filter(l => l.leadStatus === 'CONVERSION_TO_CLIENT').length;

  // Chart Data Preparation
  const pipelineChartData = useMemo(() => {
    return LEAD_STAGES.map(stage => ({
      name: stage.replace(/_/g, ' '),
      value: leads.filter(l => l.leadStatus === stage).length
    })).filter(d => d.value > 0);
  }, [leads]);

  return (
    <div className="flex flex-col gap-8 w-full font-sans text-slate-800 pb-12">
      
      {/* Header Section */}
      <section className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Sales Dashboard</h1>
          <p className="mt-1 text-sm text-slate-500 font-medium">Pipeline overview and performance metrics.</p>
        </div>
        <div className="flex gap-3 flex-wrap">
          <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-lg px-4 py-2 shadow-sm focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-100 transition-all">
            <FiMapPin className="text-slate-400" size={16} />
            <input type="text" name="region" value={filters.region} onChange={handleFilterChange} placeholder="Filter region..." className="bg-transparent text-sm text-slate-700 font-medium outline-none placeholder-slate-400 w-32" />
          </div>
        </div>
      </section>

      {/* Global Summary Metrics */}
      <section className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-5">
        <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-6 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">Total Leads</p>
              <p className="mt-2 text-2xl sm:text-3xl font-bold text-slate-900">{leads.length}</p>
            </div>
            <div className="p-2 bg-blue-50 text-blue-600 rounded-lg shrink-0">
              <FiBriefcase size={16} />
            </div>
          </div>
        </div>
        
        <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-6 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">Active Deals</p>
              <p className="mt-2 text-2xl sm:text-3xl font-bold text-slate-900">{activeLeadsCount}</p>
            </div>
            <div className="p-2 bg-blue-50 text-blue-600 rounded-lg shrink-0">
              <FiTarget size={16} />
            </div>
          </div>
        </div>
        
        <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-6 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">Deals Won</p>
              <p className="mt-2 text-2xl sm:text-3xl font-bold text-slate-900">{wonLeadsCount}</p>
            </div>
            <div className="p-2 bg-blue-50 text-blue-600 rounded-lg shrink-0">
              <FiPieChart size={16} />
            </div>
          </div>
        </div>
        
        <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-6 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">Tasks Today</p>
              <p className="mt-2 text-2xl sm:text-3xl font-bold text-slate-900">{todayFollowups.length}</p>
            </div>
            <div className="p-2 bg-slate-100 text-slate-600 rounded-lg shrink-0">
              <FiCalendar size={16} />
            </div>
          </div>
        </div>
        
        <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-6 shadow-sm hover:shadow-md transition-shadow col-span-2 sm:col-span-1">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">Overdue</p>
              <p className="mt-2 text-2xl sm:text-3xl font-bold text-slate-900">{overdueFollowups.length}</p>
            </div>
            <div className="p-2 bg-slate-100 text-slate-600 rounded-lg shrink-0">
              <FiAlertCircle size={16} />
            </div>
          </div>
        </div>
      </section>

      {/* Main Dash Grid */}
      <section className="grid gap-6 lg:grid-cols-[2fr_1fr]">
        
        {/* Left Column: Visualizations & Lists */}
        <div className="flex flex-col gap-6">
          
          {/* Pipeline Visual Chart */}
          <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-7 shadow-sm">
            <h2 className="text-sm font-bold text-slate-900 uppercase tracking-wider mb-6">Pipeline Distribution</h2>
            {loading ? (
              <div className="h-64 flex items-center justify-center animate-pulse text-slate-400">Loading chart...</div>
            ) : pipelineChartData.length === 0 ? (
              <div className="h-64 flex items-center justify-center text-slate-500 text-sm">No data matching filters.</div>
            ) : (
              <div className="h-64 w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={pipelineChartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                    <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: '#64748b', fontWeight: 600 }} />
                    <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: '#64748b', fontWeight: 600 }} />
                    <Tooltip cursor={{ fill: '#f8fafc' }} contentStyle={{ borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)', fontWeight: 'bold', color: '#0f172a', padding: '12px' }} />
                    <Bar dataKey="value" radius={[6, 6, 0, 0]} maxBarSize={48}>
                      {pipelineChartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={STAGE_COLORS[entry.name.replace(/ /g, '_')] || '#94a3b8'} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>

          {/* Leads Table Snippet */}
          <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-7 shadow-sm">
            <div className="flex items-center justify-between border-b border-slate-100 pb-5 mb-5">
              <h2 className="text-sm font-bold text-slate-900 uppercase tracking-wider">Leads</h2>
              <Link to="/sales/leads" className="text-sm font-bold text-blue-600 hover:text-blue-800 transition-colors flex items-center gap-1.5">
                View All <FiArrowRight size={14} />
              </Link>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm min-w-[420px]">
                <thead className="text-slate-400 text-xs font-bold border-b border-slate-100 uppercase tracking-wider">
                  <tr>
                    <th className="pb-4 font-bold">Lead Name</th>
                    <th className="pb-4 font-bold">Company</th>
                    <th className="pb-4 font-bold text-right">Stage</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {leads.slice(0, 5).map(lead => (
                    <tr key={lead.id} className="hover:bg-slate-50/50 transition-colors group">
                      <td className="py-4 font-bold text-slate-900">
                        <Link to={`/sales/leads/${lead.id}`} className="hover:text-blue-600 transition-colors">
                          {lead.firstName} {lead.lastName}
                        </Link>
                      </td>
                      <td className="py-4 text-slate-500 font-medium">{lead.companyName || '—'}</td>
                      <td className="py-4 text-right">
                        <span className={`text-[11px] font-bold px-3 py-1.5 rounded-full border ${
                          lead.leadStatus === 'CONVERSION_TO_CLIENT' ? 'bg-emerald-100 text-emerald-700 border-emerald-200/60' : 
                          lead.leadStatus === 'NEGOTIATIONS' ? 'bg-purple-100 text-purple-700 border-purple-200/60' : 
                          lead.leadStatus === 'MEETING' ? 'bg-amber-100 text-amber-700 border-amber-200/60' : 
                          lead.leadStatus === 'TALKS' ? 'bg-indigo-100 text-indigo-700 border-indigo-200/60' : 
                          lead.leadStatus === 'SQL' ? 'bg-blue-100 text-blue-700 border-blue-200/60' :
                          lead.leadStatus === 'MQL' ? 'bg-sky-100 text-sky-700 border-sky-200/60' :
                          'bg-slate-100 text-slate-700 border-slate-200/60'
                        }`}>
                          {lead.leadStatus === 'CONVERSION_TO_CLIENT' ? 'CONVERSION TO CLIENT' : lead.leadStatus}
                        </span>
                      </td>
                    </tr>
                  ))}
                  {leads.length === 0 && (
                    <tr><td colSpan="3" className="py-8 text-center text-slate-400 font-medium">No leads found.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Right Column: Action Center & Admin Tools */}
        <div className="flex flex-col gap-6">
          
          {/* Action Center - Urgent Followups */}
          <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden flex flex-col h-full max-h-[460px]">
            <div className="p-6 border-b border-slate-100 flex items-center gap-2.5">
            <div className="p-1.5 bg-blue-50 text-blue-600 rounded-md"><FiClock size={14} /></div>
              <h2 className="text-sm font-bold text-slate-900 uppercase tracking-wider">Action Center</h2>
            </div>
            
            <div className="p-6 overflow-y-auto flex-1 bg-slate-50/30">
              <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-4">Overdue Tasks ({overdueFollowups.length})</h3>
              <div className="space-y-3 mb-8">
                {overdueFollowups.length === 0 ? <p className="text-sm font-medium text-slate-400 italic">No overdue tasks.</p> : 
                  overdueFollowups.slice(0, 3).map(l => (
                    <Link to={`/sales/leads/${l.id}`} key={l.id} className="block p-4 rounded-xl border border-slate-200 bg-white hover:border-slate-300 hover:shadow-sm transition-all group">
                      <div className="flex justify-between items-center">
                        <div>
                          <p className="text-sm font-bold text-slate-900 group-hover:text-blue-600 transition-colors">{l.firstName} {l.lastName}</p>
                          <p className="text-xs font-medium text-slate-500 mt-0.5">{l.companyName || '—'}</p>
                        </div>
                        <FiArrowRight size={16} className="text-slate-300 group-hover:text-blue-600 transition-colors" />
                      </div>
                    </Link>
                  ))
                }
              </div>

              <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-4">Due Today ({todayFollowups.length})</h3>
              <div className="space-y-3">
                {todayFollowups.length === 0 ? <p className="text-sm font-medium text-slate-400 italic">No tasks for today.</p> : 
                  todayFollowups.slice(0, 3).map(l => (
                    <Link to={`/sales/leads/${l.id}`} key={l.id} className="block p-4 rounded-xl border border-slate-200 bg-white hover:border-slate-300 hover:shadow-sm transition-all group">
                      <div className="flex justify-between items-center">
                        <div>
                          <p className="text-sm font-bold text-slate-900 group-hover:text-blue-600 transition-colors">{l.firstName} {l.lastName}</p>
                          <p className="text-xs font-medium text-slate-500 mt-0.5">{l.companyName || '—'}</p>
                        </div>
                        <FiArrowRight size={16} className="text-slate-300 group-hover:text-blue-600 transition-colors" />
                      </div>
                    </Link>
                  ))
                }
              </div>
            </div>
          </div>

          {/* Admin Tools: Deep Insights */}
          {isAdmin && (
            <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
               <div className="p-6 border-b border-slate-100 flex items-center gap-2.5">
                 <div className="p-1.5 bg-blue-50 text-blue-600 rounded-md"><FiUsers size={14} /></div>
                 <h2 className="text-sm font-bold text-slate-900 uppercase tracking-wider">Team Insights</h2>
               </div>
               <div className="p-6">
                 <div className="grid grid-cols-2 gap-4 mb-8">
                   <div className="bg-slate-50 rounded-xl p-4 border border-slate-100">
                     <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">Avg Win Rate</p>
                     <p className="text-2xl font-black text-slate-900 mt-2">{detailedStats?.conversionRate ?? '0.0%'}</p>
                   </div>
                   <div className="bg-slate-50 rounded-xl p-4 border border-slate-100">
                     <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">Regions Active</p>
                     <p className="text-2xl font-black text-slate-900 mt-2">{regionStats.length}</p>
                   </div>
                 </div>
                 
                 <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-4">Top Performers</h3>
                 <div className="space-y-4">
                   {performance.slice(0, 3).map((p, i) => (
                     <div key={i} className="flex justify-between items-center text-sm">
                       <div className="flex items-center gap-3">
                         <div className="w-6 h-6 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center font-bold text-xs">{i + 1}</div>
                         <span className="font-bold text-slate-900">{p.memberName || p.employee?.username || p.username || 'Unknown'}</span>
                       </div>
                       <span className="font-bold text-slate-500 bg-slate-100 px-2.5 py-1 rounded-md">{p.conversions ?? p.value ?? p.count ?? 0} won</span>
                     </div>
                   ))}
                   {performance.length === 0 && <p className="text-sm font-medium text-slate-400 italic">No performance data yet.</p>}
                 </div>
               </div>
            </div>
          )}

        </div>
      </section>
    </div>
  );
}
